import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import ccmService from '../../services/ccmService';
import './Timer.css';

const Timer = () => {
  const [loading, setLoading] = useState(false);
  const [activeTimers, setActiveTimers] = useState([]);
  const [timeCaptureReasons, setTimeCaptureReasons] = useState([]);
  const [newTimer, setNewTimer] = useState({
    enrollment_id: '',
    time_capture_reason_id: '',
    description: ''
  });
  const [timerIntervals, setTimerIntervals] = useState({});

  useEffect(() => {
    fetchTimeCaptureReasons();
    fetchActiveTimers();
    
    // Set up interval to update timers every second
    const interval = setInterval(() => {
      fetchActiveTimers();
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchTimeCaptureReasons = async () => {
    try {
      const response = await ccmService.getTimeCaptureReasons();
      if (response.success) {
        setTimeCaptureReasons(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error fetching time capture reasons:', error);
    }
  };

  const fetchActiveTimers = async () => {
    try {
      // Get user ID from session or context
      const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
      if (userId) {
        const response = await ccmService.getActiveTimers(userId);
        if (response.success) {
          setActiveTimers(response.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching active timers:', error);
    }
  };

  const handleStartTimer = async () => {
    if (!newTimer.enrollment_id) {
      alert('Please enter an Enrollment ID');
      return;
    }
    
    try {
      setLoading(true);
      const response = await ccmService.startTimer(newTimer);
      if (response.success) {
        setNewTimer({ enrollment_id: '', time_capture_reason_id: '', description: '' });
        fetchActiveTimers();
      }
    } catch (error) {
      console.error('Error starting timer:', error);
      alert('Failed to start timer: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleStopTimer = async (sessionId, description = '') => {
    try {
      setLoading(true);
      const response = await ccmService.stopTimer(sessionId, description);
      if (response.success) {
        fetchActiveTimers();
        alert('Timer stopped and time logged successfully');
      }
    } catch (error) {
      console.error('Error stopping timer:', error);
      alert('Failed to stop timer: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes) => {
    if (!minutes) return '00:00:00';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const secs = 0; // We're tracking minutes, not seconds
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <Container fluid className="ccm-timer-container">
      <Row>
        <Col>
          <h3 className="mb-4">CCM Timer</h3>
        </Col>
      </Row>

      {/* Active Timers */}
      {activeTimers.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>
                <h5>Active Timers</h5>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th>Reason</th>
                      <th>Elapsed Time</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeTimers.map((timer) => (
                      <tr key={timer.id}>
                        <td>{timer.patient_name || 'N/A'}</td>
                        <td>{timer.time_capture_reason || 'N/A'}</td>
                        <td>
                          <Badge bg="info" style={{ fontSize: '1.2em', padding: '0.5em' }}>
                            {formatTime(timer.current_elapsed_time || timer.elapsed_time)}
                          </Badge>
                        </td>
                        <td>{timer.description || 'N/A'}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                              const desc = prompt('Enter description (optional):');
                              handleStopTimer(timer.id, desc || '');
                            }}
                          >
                            Stop & Save
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Start New Timer */}
      <Row>
        <Col md={8}>
          <Card>
            <Card.Header>
              <h5>Start New Timer</h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Enrollment ID *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Enrollment ID"
                    value={newTimer.enrollment_id}
                    onChange={(e) => setNewTimer({ ...newTimer, enrollment_id: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Time Capture Reason</Form.Label>
                  <Form.Select
                    value={newTimer.time_capture_reason_id}
                    onChange={(e) => setNewTimer({ ...newTimer, time_capture_reason_id: e.target.value })}
                  >
                    <option value="">Select Reason (Optional)</option>
                    {(Array.isArray(timeCaptureReasons) ? timeCaptureReasons : []).map((reason) => (
                      <option key={reason.id} value={reason.id}>
                        {reason.reason_description}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={newTimer.description}
                    onChange={(e) => setNewTimer({ ...newTimer, description: e.target.value })}
                    placeholder="Optional description of the service"
                  />
                </Form.Group>
                <Button
                  variant="primary"
                  onClick={handleStartTimer}
                  disabled={loading || !newTimer.enrollment_id}
                >
                  {loading ? 'Starting...' : 'Start Timer'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Header>
              <h5>Instructions</h5>
            </Card.Header>
            <Card.Body>
              <ul>
                <li>Enter the Enrollment ID for the patient</li>
                <li>Select a time capture reason (optional)</li>
                <li>Add a description (optional)</li>
                <li>Click "Start Timer" to begin tracking time</li>
                <li>Click "Stop & Save" when finished to log the time</li>
                <li>Minimum time is 1 minute</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Timer;

